import { Link } from "react-router-dom";
import { ArrowUpRight, Download } from "lucide-react";
import MarketCard from "@/components/MarketCard";
import { MARKETS } from "@/data/catalogue";

const Markets = () => {
  return (
    <div data-testid="page-markets" className="pt-[72px]">
      <section className="container-wide py-16 md:py-28">
        <p className="eyebrow mb-5">◦  Target Export Corridors · July 2027</p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-brand-black tracking-tight leading-[1.05] max-w-4xl">
          Preparing supply for <span className="italic text-brand-teal">20+ target markets.</span>
        </h1>
        <p className="mt-5 md:mt-6 text-brand-black/70 max-w-2xl leading-relaxed text-sm md:text-base">
          India's expanding FTA network — EU, UAE, Japan, Australia, South Korea — positions SPIRUVA for preferential or zero-tariff access ahead of July 2027 commercial readiness. Pair that with our multi-certification stack and the friction comes out of premium-blue procurement planning.
        </p>
      </section>

      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-14 md:py-20">
          <div className="max-w-3xl mb-10 md:mb-12">
            <p className="eyebrow mb-4">◦  India to the World</p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight">
              India's <span className="italic text-brand-teal">trade-agreement edge.</span>
            </h2>
            <p className="text-brand-black/70 mt-5 leading-relaxed text-sm md:text-base">
              India's growing network of Free Trade Agreements creates preferential tariff access that other supply geographies cannot match. Each corridor below represents a target market we are preparing for July 2027 launch — not a list of currently-active customers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {MARKETS.map((m) => <MarketCard key={m.code} market={m} />)}
          </div>
        </div>
      </section>

      <section className="container-wide py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-center">
        <div>
          <p className="eyebrow mb-4">◦  Compliance Stack</p>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight">
            Documentation, <span className="italic text-brand-teal">pre-cleared.</span>
          </h2>
          <p className="text-brand-black/70 mt-5 md:mt-6 leading-relaxed max-w-lg text-sm md:text-base">
            Our compliance framework is being assembled around what global buyers actually need at port-of-entry. From APEDA RCMC to FDA prior-notice readiness, EU CHED-D issuance, and regional listings — the export desk is structured to remove friction before it appears.
          </p>
          <Link to="/distributor-portal" data-testid="markets-cta-export" className="btn-primary mt-8 min-h-[48px]">
            Talk to Export Desk <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-px bg-brand-green/15">
          {[
            { k: "APEDA RCMC", v: "Active" },
            { k: "USFDA Prior Notice", v: "Framework Ready" },
            { k: "EU CHED-D", v: "Issuance Pathway" },
            { k: "TGA Listing (AU)", v: "In Preparation" },
            { k: "KFDA / MFDS (KR)", v: "In Preparation" },
            { k: "ESMA (UAE)", v: "In Preparation" },
          ].map((x) => (
            <div key={x.k} className="bg-white p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45">{x.k}</p>
              <p className="font-display text-base sm:text-lg text-brand-teal mt-1">{x.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RESOURCES TEASER STRIP */}
      <section className="bg-brand-cream border-t border-brand-green/15" data-testid="markets-resources-teaser">
        <div className="container-wide py-14 md:py-20">
          <div className="flex items-end justify-between gap-6 mb-8 md:mb-10 flex-wrap">
            <div>
              <p className="eyebrow mb-3">◦  Buyer-facing briefings</p>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-brand-black leading-tight tracking-tight max-w-2xl">
                Use cases &amp; fact sheets <span className="italic text-brand-teal">for procurement teams.</span>
              </h2>
            </div>
            <Link to="/resources" data-testid="markets-cta-resources" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-teal hover:text-brand-gold transition">
              All resources <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {[
              { id: "food-beverage", kind: "Use Case", label: "Food & Beverage" },
              { id: "compliance", kind: "Fact Sheet", label: "Compliance & Documentation" },
              { id: "india-origin", kind: "Fact Sheet", label: "Why India" },
            ].map((r) => (
              <Link
                key={r.id}
                to="/resources"
                data-testid={`markets-teaser-${r.id}`}
                className="bg-white border border-brand-green/15 p-5 hover:border-brand-gold transition group flex items-center justify-between gap-3"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50">{r.kind}</p>
                  <p className="font-display text-lg text-brand-black mt-1">{r.label}</p>
                </div>
                <Download size={16} className="text-brand-black/40 group-hover:text-brand-gold transition" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Markets;
